import VideoModel from "../model/video.js";

import VideoView from "../view/video.js";

import Animation from "../../animation/animation.js";

import {
  render,
  remove,
  RenderPosition
} from "../../render/render.js";

import {
  formatTime
} from "../helpers.js";

import {
  getMaxZIndex
} from "../../../utils/get-max-z-index.js";

import {
  APPEARANCE_DURATION,
  defaultVideoModelData,
  ActionType,
  MOBILE_MEDIA_QUERY,
  YM_COUNTER_ID,
  YM_STORAGE_PREFIX,
  WATCH_GOAL_SECONDS
} from "../const.js";

import {
  AnimationClass
} from "../../animation/const.js";

export default class Video {
  constructor(options = {}) {
    this._options = options;

    this._watchTimer = null;
    this._sessionStorage = sessionStorage;

    this._videoModel = new VideoModel(defaultVideoModelData);
    this._videoView = new VideoView(options);
    this._animation = new Animation();

    this._handleCanplay = this._handleCanplay.bind(this);
    this._handleClick = this._handleClick.bind(this);
    this._handleChangeVideoModel = this._handleChangeVideoModel.bind(this);
    this._handleSoundClick = this._handleSoundClick.bind(this);
    this._handleTimeUpdate = this._handleTimeUpdate.bind(this);
    this._handleCloseClick = this._handleCloseClick.bind(this);

    this._videoModel.addObserver(this._handleChangeVideoModel);
  }

  init() {
    if (this._sessionStorage.promoVideoClosed === `true`) {
      return;
    }

    this._renderElement();
  }

  _isMobile() {
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  }

  _renderElement() {
    if (this._isMobile()) {
      this._options.isMobile = true;
    }

    const renderClass = (this._options.renderBefore) ?
      this._options.renderBefore :
      this._options.renderAfter;

    const container = (renderClass) ?
      document.querySelector(renderClass) :
      document.body;
    const renderPosition = (this._options.renderBefore) ?
      RenderPosition.BEFOREBEGIN :
      RenderPosition.AFTEREND;

    const videoSectionElement = this._videoView.getElement();

    if (!this._isMobile()) {
      const maxZIndex = getMaxZIndex();
      this._videoView.setZIndex(maxZIndex + 1);
    }

    render(container, videoSectionElement, renderPosition);
    this._videoView.setCanplayHandler(this._handleCanplay);

    if (this._options.isMobile === true) {
      this._handleCanplay();
    }
  }

  _startWatchTimer() {
    const startTime = Date.now();

    this._watchTimer = setInterval(() => {
      const currentTime = Date.now();
      const spentTime = (currentTime - startTime) / 1000;

      if (spentTime > WATCH_GOAL_SECONDS) {
        this._reachMetrikaGoal(`promo_video_watched_more_than_one_minute`);
        clearInterval(this._watchTimer);
      }
    }, 1000);
  }

  _stopWatchTimer() {
    clearInterval(this._watchTimer);
  }

  _processVideoLoad() {
    const videoSectionElement = this._videoView.getElement();

    this._videoView.setWrapperVisible();

    this._animation.fadeIn(
        videoSectionElement,
        APPEARANCE_DURATION,
        () => {
          this._videoView.setRendered();
          this._videoView.setClickHandler(this._handleClick);
          this._videoView.setSoundClickHandler(this._handleSoundClick);
          this._videoView.setCloseClickHandler(this._handleCloseClick);
        },
        AnimationClass.SHOW_FLEX
    );
  }

  _processVideoExpand() {
    if (this._isMobile()) {
      this._videoView.play();

      const maxZIndex = getMaxZIndex(true);
      this._videoView.setWrapperZIndex(maxZIndex + 1);
    }

    this._videoView.expand(this._isMobile());
    this._videoView.setTimeUpdateHandler(this._handleTimeUpdate);

    this._reachMetrikaGoal(`promo_video_click`);

    this._startWatchTimer();
  }

  _processVideoCollapse() {
    if (this._isMobile()) {
      this._videoView.pause();
    }

    if (!this._videoModel.getData().muted) {
      this._videoModel.changeData(Object.assign(
          {},
          this._videoModel.getData(),
          {
            muted: true
          }
      ), ActionType.MUTE);
    }

    this._videoView.removeWrapperZIndex();
    this._videoView.collapse();
    this._videoView.removeTimeUpdateHandler();
    this._stopWatchTimer();
  }

  _processUnmute() {
    this._reachMetrikaGoal(`promo_video_unmute`);
    this._videoView.unmute();
  }

  _processMute() {
    this._videoView.mute();
  }

  _processChangeTime(currentTime, lastTime) {
    const elapsedTime = parseInt(lastTime, 10) - parseInt(currentTime, 10);
    const minutes = Math.floor(elapsedTime / 60);
    const seconds = elapsedTime - minutes * 60;

    const formatedTime = `${formatTime(minutes.toString(), `0`, 2)}:${formatTime(seconds.toString(), `0`, 2)}`;

    this._videoView.setElapsedTime(formatedTime);

    if (!this._videoModel.getData().onceStarted) {
      this._videoModel.changeData(Object.assign(
          {},
          this._videoModel.getData(),
          {
            onceStarted: true
          }
      ), ActionType.ONCE_STARTED);
    }
  }

  _processDestroy() {
    this._animation.fadeOut(
        this._videoView.getElement(),
        APPEARANCE_DURATION,
        () => {
          remove(this._videoView);
        }
    );

    this._reachMetrikaGoal(`promo_video_close`);
    this._sessionStorage.promoVideoClosed = true;
  }

  _processOnceStarted() {
    this._videoView.setOnceStarted();
  }

  _reachMetrikaGoal(goalId) {
    if (localStorage.getItem(YM_STORAGE_PREFIX + goalId)) {
      return;
    }

    if (window.ym) {
      window.ym(YM_COUNTER_ID, `reachGoal`, goalId);
      localStorage.setItem(YM_STORAGE_PREFIX + goalId, true);
    }
  }

  _handleChangeVideoModel(action, payload) {
    switch (action) {
      case ActionType.LOAD:
        this._processVideoLoad();
        break;
      case ActionType.EXPAND:
        this._processVideoExpand();
        break;
      case ActionType.COLLAPSE:
        this._processVideoCollapse();
        break;
      case ActionType.UNMUTE:
        this._processUnmute();
        break;
      case ActionType.MUTE:
        this._processMute();
        break;
      case ActionType.CHANGE_TIME:
        this._processChangeTime(payload.currentTime, payload.lastTime);
        break;
      case ActionType.DESTROY:
        this._processDestroy();
        break;
      case ActionType.ONCE_STARTED:
        this._processOnceStarted();
        break;
    }
  }

  _handleCanplay() {
    this._videoModel.changeData(Object.assign(
        {},
        this._videoModel.getData(),
        {
          loaded: true
        }
    ), ActionType.LOAD);
  }

  _handleCloseClick() {
    const videoModelData = this._videoModel.getData();

    const action = (videoModelData.expanded === true) ?
      ActionType.COLLAPSE :
      ActionType.DESTROY;

    this._videoModel.changeData(Object.assign(
        {},
        videoModelData,
        {
          expanded: false
        }
    ), action);
  }

  _handleClick(evt) {
    if (evt.target === this._videoView.getCloseElement()) {
      return;
    }

    const videoModelData = this._videoModel.getData();

    if (
      videoModelData.loaded === false ||
      videoModelData.expanded === true
    ) {
      return;
    }

    const action = ActionType.EXPAND;

    this._videoModel.changeData(Object.assign(
        {},
        videoModelData,
        {
          expanded: !videoModelData.expanded
        }
    ), action);
  }

  _handleSoundClick() {
    const videoModelData = this._videoModel.getData();

    if (videoModelData.loaded === false) {
      return;
    }

    const action = (videoModelData.muted === true) ?
      ActionType.UNMUTE :
      ActionType.MUTE;

    this._videoModel.changeData(Object.assign(
        {},
        videoModelData,
        {
          muted: !videoModelData.muted
        }
    ), action);
  }

  _handleTimeUpdate(currentTime, lastTime) {
    if (this._videoModel.getData().expanded === false) {
      return;
    }

    this._videoModel.changeData(Object.assign(
        {},
        this._videoModel.getData()
    ), ActionType.CHANGE_TIME, {
      currentTime,
      lastTime
    });
  }
}
